/**
 * Renders one archived email's inert plain-text body on demand. The component
 * owns request cancellation and retry state so expanding a row never blocks
 * or replaces the parent grid's loading state.
 */
import React, { useCallback, useEffect, useState } from "react";
import Button from "devextreme-react/button";
import LoadIndicator from "devextreme-react/load-indicator";
import {
	ARCHIVED_EMAIL_ERROR_CODES,
	ARCHIVED_EMAIL_ERROR_MESSAGES,
	isArchivedEmailCancellationError,
} from "../../api/email/archivedEmailsApi";

const LOADING_MESSAGE = "Loading email body...";
const EMPTY_BODY_MESSAGE = "No email body is available.";

/**
 * Maps the stable detail error category to an approved UI message.
 *
 * @param {*} error Sanitized detail-loader error.
 * @returns {string} Fixed user-facing message.
 */
export const getArchivedEmailDetailErrorMessage = (error) =>
	error?.code === ARCHIVED_EMAIL_ERROR_CODES.NOT_FOUND
		? ARCHIVED_EMAIL_ERROR_MESSAGES.notFound
		: ARCHIVED_EMAIL_ERROR_MESSAGES.detail;

/**
 * Lazily displays one archived email's inert plain-text body.
 *
 * @param {object} props Component properties.
 * @param {object} props.email Expanded summary row.
 * @param {Function} props.loadDetailAsync Task 04 cached detail loader.
 * @returns {JSX.Element} Accessible loading, error, empty, or body view.
 */
const ArchivedEmailDetail = ({ email, loadDetailAsync }) => {
	const [attempt, setAttempt] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [plainTextBody, setPlainTextBody] = useState("");
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		const controller = new AbortController();
		let isMounted = true;

		setIsLoading(true);
		setErrorMessage("");

		loadDetailAsync(email?.id, { signal: controller.signal })
			.then((detail) => {
				if (!isMounted || controller.signal.aborted) return;
				setPlainTextBody(detail?.plainTextBody ?? "");
			})
			.catch((error) => {
				if (
					!isMounted ||
					controller.signal.aborted ||
					isArchivedEmailCancellationError(error)
				) {
					return;
				}

				setPlainTextBody("");
				setErrorMessage(
					getArchivedEmailDetailErrorMessage(error)
				);
			})
			.finally(() => {
				if (isMounted && !controller.signal.aborted) {
					setIsLoading(false);
				}
			});

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, [attempt, email?.id, loadDetailAsync]);

	/**
	 * Starts a new request. Task 04 removes failed requests from its in-flight
	 * map, so retry cannot be satisfied by a rejected cached promise.
	 */
	const retry = useCallback(() => {
		setAttempt((current) => current + 1);
	}, []);

	const accessibleSubject =
		typeof email?.subject === "string" && email.subject.trim()
			? email.subject
			: "archived email";
	const hasBody = plainTextBody.trim().length > 0;

	return (
		<section
			className="archived-email-detail"
			role="region"
			aria-label={`Email body for ${accessibleSubject}`}
		>
			{isLoading && (
				<div
					className="archived-email-detail-status"
					role="status"
					aria-live="polite"
				>
					<LoadIndicator
						width={24}
						height={24}
						visible={true}
					/>
					<span>{LOADING_MESSAGE}</span>
				</div>
			)}

			{!isLoading && errorMessage && (
				<div
					className="archived-email-detail-error"
					role="alert"
				>
					<span>{errorMessage}</span>
					<Button
						text="Retry"
						type="default"
						stylingMode="contained"
						onClick={retry}
					/>
				</div>
			)}

			{!isLoading && !errorMessage && !hasBody && (
				<div className="archived-email-detail-empty">
					{EMPTY_BODY_MESSAGE}
				</div>
			)}

			{!isLoading && !errorMessage && hasBody && (
				<div className="archived-email-body">
					{plainTextBody}
				</div>
			)}
		</section>
	);
};

export default ArchivedEmailDetail;
